import React from 'react';
import { EnhancedEventCard } from '@eb/eds-event-card';

import { useUser, useLazyImages } from '../../hooks';
import {
    useSaveEvent,
    useSaveEventToCollection,
    useCreateCollection,
    useEventCollections,
    useShouldSaveFromLoggedOutRedirect,
    useAffCode,
} from './hooks';
import { EventCardProps } from './types';
import {
    trackEventClick,
    trackSaveEvent,
    trackShowCreateCollectionView,
    trackShowAddToCollectionView,
    trackSaveEventToCollection,
    trackCreateCollection,
} from './analytics';

export const EventCard: React.FunctionComponent<EventCardProps> = (props) => {
    //Initialize document event listener for lazy loading images
    const { shouldUseLazyImages } = useLazyImages(props.isLazyImage);

    //Local state
    const [saved, setSaved] = React.useState(!!props.eventData.savedByYou);

    //Data fetchers
    const user = useUser({ user: props.user });
    const {
        eventCollections,
        refetch: fetchEventCollections,
    } = useEventCollections(props.eventData.id, user?.collections || [], {
        enabled: false,
    });

    //Action handlers and tracking helpers
    const handleSaveEvent = useSaveEvent({
        setSaved,
        onCompleted: () => {
            props.onSave?.(saved, props.eventData.id);

            props.gaCategory &&
                props.isAuthenticated &&
                trackSaveEvent(props.gaCategory, !saved, props.eventData.id);
        },
    });

    //Currently does not display success notification
    //However save is successful
    //TODO: Ensure notification fires
    useShouldSaveFromLoggedOutRedirect(
        handleSaveEvent,
        props.eventData.id,
        props.isAuthenticated,
    );

    const handleSaveEventToCollection = useSaveEventToCollection({
        onCompleted: ({
            collectionId,
            isChecked,
        }: {
            collectionId: string;
            isChecked: boolean;
        }) => {
            trackSaveEventToCollection(
                props.gaCategory,
                isChecked,
                collectionId,
            );
            props.onSaveEventToCollection?.({
                isChecked,
                collectionId,
                eventId: props.eventData.id,
            });
        },
    });

    const handleCreateCollection = useCreateCollection({
        userId: user?.id || '',
        onCompleted: (collectionId: string) => {
            trackCreateCollection(props.gaCategory, collectionId);
            props.onCreateCollection?.(name);
        },
    });

    const handleOnClick = () => {
        props.gaCategory &&
            trackEventClick(props.gaCategory, props.eventData.id);
        props.onClick?.(props.eventData.id);
    };

    const handleShowCreateCollection = () => {
        props.gaCategory && trackShowCreateCollectionView(props.gaCategory);
        props.onShowCreateCollectionView?.();
    };

    const handleShowAddToCollection = () => {
        props.gaCategory && trackShowAddToCollectionView(props.gaCategory);
        props.onShowAddToCollectionView?.();

        user.fetchUserData().then(() => {
            if (!eventCollections) {
                return fetchEventCollections();
            }
        });
    };

    const { eventData, currencyFormat, ...otherProps } = props;

    if (eventData.minPrice && currencyFormat) {
        eventData.minPrice.currencyFormat = currencyFormat;
    }

    eventData.url = useAffCode(eventData.url, props.affCode || '');

    return (
        <EnhancedEventCard
            {...otherProps}
            {...eventData}
            isLazyImage={shouldUseLazyImages && props.isLazyImage}
            onAddOrRemoveEventToCollection={(data: {
                isChecked: boolean;
                value: string;
            }) =>
                handleSaveEventToCollection({
                    isChecked: data.isChecked,
                    collectionId: data.value,
                    eventId: props.eventData.id,
                })
            }
            onClick={handleOnClick}
            onCreateCollection={(name: string) =>
                handleCreateCollection({ name, eventId: props.eventData.id })
            }
            onSaveEvent={() =>
                handleSaveEvent({
                    eventId: props.eventData.id,
                    isAuthenticated: !!props.isAuthenticated,
                    savedByYou: saved,
                })
            }
            onShowAddToCollectionView={handleShowAddToCollection}
            onShowCreateCollectionView={handleShowCreateCollection}
            savedByYou={saved}
            userCollections={user.collections || []}
            eventCollections={eventCollections || []}
        />
    );
};
